import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import { PLOT_HEIGHT, cardState, spanClass, ticksFor, type WidgetBlockProps } from '../shared/cardShell';

export type MentionVolumeVsCompetitorsView = {
  /** `Your mentions`, `Category total`, `Busiest day`. */
  kpis: KPIMetric[];
  /** One row per bucket, one key per brand (mention counts). */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type MentionVolumeVsCompetitorsCardProps = WidgetBlockProps & {
  view?: MentionVolumeVsCompetitorsView;
  /** Opens the conversations behind a bucket (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `931:36991` — the share trend's own rows, drawn as counts.
 *
 * Together with `Share of voice over time` it is the pair: a share can fall
 * while the volume under it rises, because the category grew faster.
 *
 * **Not indexed**, and that is a measurement rather than a preference: mentions
 * per brand per day on this corpus have medians of 5, 3, 3, 2 and 1, and a count
 * whose typical value is one cannot be indexed, because 1 → 2 is +100%.
 */
export function MentionVolumeVsCompetitorsCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: MentionVolumeVsCompetitorsCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Mention volume vs competitors"
      helpText="Daily mentions for every selected brand, as counts rather than shares."
      helpSupportingText="The same rows the share chart above draws. Read the two together: when every line lifts on the same day the category moved, not us — and a brand's share can fall on a day its volume rose, because someone else's rose faster."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
